import { useState } from "react";
import { Activity } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import AppCard from "../../components/AppCard";
import Button from "../../components/ui/Button";
import SocialAuthButtons from "../../components/SocialAuthButtons";
import { useAuth } from "../../providers/AuthProvider";
import RequestResetDialog from "./RequestResetDialog";
import CompleteResetDialog from "./CompleteResetDialog";

interface LoginFormValues {
  email: string;
  password: string;
}

const formatAuthError = (reason: string | null): string | null => {
  if (!reason) {
    return null;
  }

  if (reason === "invalid_oauth_callback") {
    return "Google sign-in could not be completed. Please try again.";
  }

  if (reason === "email_not_verified") {
    return "Your Google account email must be verified before signing in.";
  }

  if (reason === "google_auth_failed" || reason.includes("invalid_grant")) {
    return "Google sign-in could not be completed. Please try again.";
  }

  if (reason === "microsoft_auth_failed") {
    return "Microsoft sign-in could not be completed. Please try again.";
  }

  return "Authentication failed. Please try again.";
};

const inputClassName =
  "h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";

export default () => {
  const { session, isLoading, refreshSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isRequestResetOpen, setRequestResetOpen] = useState(false);
  const authError = searchParams.get("auth") === "error" ? formatAuthError(searchParams.get("reason")) : null;
  const resetToken = searchParams.get("token");
  const resetIntent = searchParams.get("reset");
  const resetEmail = searchParams.get("email") ?? "";
  const urlTriggeredCompleteReset = resetIntent === "password" && Boolean(resetToken);
  const [didDismissCompleteReset, setDidDismissCompleteReset] = useState(false);
  const isCompleteResetOpen = urlTriggeredCompleteReset && !didDismissCompleteReset;
  const loginForm = useForm<LoginFormValues>({
    defaultValues: { email: "", password: "" },
  });

  if (session) {
    return <Navigate to="/dashboard" replace />;
  }

  const submitLogin = loginForm.handleSubmit(async values => {
    setPasswordError(null);

    const response = await fetch("/api/auth/password/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(values),
    });
    const result = await response.json().catch(() => null) as { error?: string } | null;

    if (!response.ok) {
      setPasswordError(result?.error ?? "Authentication failed.");
      return;
    }

    await refreshSession();
    navigate("/dashboard", { replace: true });
  });

  const closeResetDialogs = () => {
    setRequestResetOpen(false);
    setDidDismissCompleteReset(true);

    if (resetToken || resetIntent) {
      navigate("/user-login", { replace: true });
    }
  };

  const handleResetRequest = async (values: { email: string }): Promise<{ type: "error" | "success"; text: string }> => {
    const response = await fetch("/api/auth/password/reset/request", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(values),
    });
    const result = await response.json().catch(() => null) as { error?: string; message?: string } | null;

    if (!response.ok) {
      return { type: "error", text: result?.error ?? "Unable to send password reset email." };
    }

    return { type: "success", text: result?.message ?? "If an account exists for that email, a password reset email has been sent." };
  };

  const handleCompleteResetSuccess = async () => {
    await refreshSession();
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-slate-900 selection:bg-indigo-100">
      <div className="relative w-full max-w-[440px] space-y-8">
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-600 shadow-sm">
            <Activity className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Quantira</h1>
          <p className="mt-2 text-xs font-medium text-slate-500">Business Intelligence Platform</p>
        </div>

        <AppCard className="border-slate-200 p-8 shadow-sm">
          <div className="space-y-6">
            {(authError || passwordError) && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {passwordError ?? authError}
              </div>
            )}

            <div className="space-y-2 text-center">
              <h2 className="text-lg font-semibold text-slate-900">Sign in to Quantira</h2>
              <p className="text-sm text-slate-500">Use email/password or continue with a provider.</p>
            </div>

            <form className="space-y-4" onSubmit={event => void submitLogin(event)}>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700" htmlFor="login-email">Email</label>
                <input id="login-email" type="email" autoComplete="email" className={inputClassName} {...loginForm.register("email", { required: true })} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700" htmlFor="login-password">Password</label>
                <input id="login-password" type="password" autoComplete="current-password" className={inputClassName} {...loginForm.register("password", { required: true })} />
              </div>
              <Button type="submit" className="h-12 w-full" disabled={isLoading || loginForm.formState.isSubmitting}>
                {loginForm.formState.isSubmitting ? "Signing in..." : "Sign in with email"}
              </Button>
              <button
                type="button"
                className="w-full text-sm font-medium text-indigo-600 transition hover:text-indigo-500"
                onClick={() => { setRequestResetOpen(true); }}
              >
                Reset password
              </button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs font-medium uppercase tracking-wide text-slate-400">
                <span className="bg-white px-3">Or</span>
              </div>
            </div>

            <SocialAuthButtons returnTo="/dashboard" disabled={isLoading || loginForm.formState.isSubmitting} />
          </div>
        </AppCard>

        <p className="text-center text-sm text-slate-500">
          Don&apos;t have an account?{" "}
          <Link to="/signup" className="font-medium text-indigo-600 transition hover:text-indigo-500">
            Create one
          </Link>
        </p>
      </div>

      {isRequestResetOpen && (
        <RequestResetDialog
          defaultEmail={loginForm.getValues("email")}
          inputClassName={inputClassName}
          onSubmit={handleResetRequest}
          onClose={closeResetDialogs}
        />
      )}

      {isCompleteResetOpen && resetToken && (
        <CompleteResetDialog
          defaultEmail={resetEmail}
          resetToken={resetToken}
          inputClassName={inputClassName}
          onSuccess={handleCompleteResetSuccess}
          onClose={closeResetDialogs}
        />
      )}
    </div>
  );
};
